import {useEffect, useState} from 'react';
import {Paginator} from 'src/design/organisms/Pagination';

import {Box} from 'src/design/atoms/Box';
import {Space} from 'src/design/atoms/Space';
import type {SourceEntityRecord} from 'src/api/ProactApiClient';
import {EntityTypeEnum} from 'src/api/ProactApiClient';
import {CheckboxRow} from 'src/design/molecules/CheckboxRow';
import {TableBodyRender} from 'src/design/molecules/TableBodyRender';
import {useGetUserGroupListQuery} from 'src/redux/queries/userGroup';

export interface UserGroupListProps {
  selected: SourceEntityRecord[];
  setSelected: React.Dispatch<React.SetStateAction<SourceEntityRecord[]>>;
  showOnlySelected: boolean;
  showSelectedAll: boolean;
  search?: string;
  itemsPerPage?: number;
  /** Callback for unfiltered total count (used for percentage calculation) */
  onTotalCountChange?: (count: number) => void;
  /** Filter user groups by project access */
  projectId?: number;
}

export const UserGroupList: React.FC<UserGroupListProps> = ({
  selected,
  setSelected,
  showOnlySelected,
  showSelectedAll,
  search,
  itemsPerPage = 25,
  onTotalCountChange,
  projectId,
}) => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const selectedIds = selected.map(i => Number(i.entityId));

  // Baseline query for unfiltered total count (used for percentage calculation)
  const {data: baselineData} = useGetUserGroupListQuery({
    active: true,
    projectIds: projectId ? [projectId] : undefined,
    start: 0,
    length: 0,
  });

  // Filtered query for display and pagination
  const {
    data: usergroups,
    isFetching: isUserGroupsFetching,
    isLoading: isUserGroupsLoading,
  } = useGetUserGroupListQuery({
    search: search,
    start: currentPage * itemsPerPage - itemsPerPage,
    length: itemsPerPage,
    active: true,
    whitelistIds: showOnlySelected ? selectedIds : undefined,
    projectIds: projectId ? [projectId] : undefined,
  });

  const isLoading = isUserGroupsFetching || isUserGroupsLoading;

  // Report unfiltered total count for percentage calculation
  useEffect(() => {
    if (onTotalCountChange && baselineData?.totalRecords !== undefined) {
      onTotalCountChange(baselineData.totalRecords);
    }
  }, [baselineData?.totalRecords, onTotalCountChange]);

  function isSelected(id: number): boolean {
    return (
      selected.filter(element => element.entityId.toString() === id.toString())
        .length > 0 || showSelectedAll
    );
  }

  const handleSelect = (e: React.ChangeEvent<HTMLInputElement>, id: number) => {
    const {checked} = e.target;
    if (checked) {
      setSelected([
        ...selected,
        {
          entityId: id,
          entityType: EntityTypeEnum.Group,
        },
      ]);
    } else {
      setSelected(selected.filter(i => i.entityId !== id));
    }
  };

  const handlePageChange = (_: unknown, value: number) => {
    setCurrentPage(value);
  };

  return (
    <Box>
      <Paginator
        mt={8}
        mb={16}
        currentPage={currentPage}
        totalRecords={usergroups?.totalRecords ?? 0}
        perPage={itemsPerPage}
        showRecordCount={false}
        onChange={handlePageChange}
      />
      {!isLoading ? (
        usergroups?.data ? (
          usergroups.data.map(item => (
            <CheckboxRow
              key={item.id}
              id={String(item.id)}
              label={item.name}
              checked={isSelected(item.id)}
              onChange={e => handleSelect(e, Number(item.id))}
              disabled={showSelectedAll}
            />
          ))
        ) : (
          <Box mt="lg">No selections found</Box>
        )
      ) : (
        <>
          <TableBodyRender isLoading={isLoading} />
          <Space mb="xl" />
        </>
      )}
    </Box>
  );
};
